<script setup lang="ts">
import ContactAddress from "@/components/ContactAddress.vue"
import VarsityInlineConfirm from "@/components/VarsityInlineConfirm.vue"
import VarsityInlineConfirmButton from "@/components/VarsityInlineConfirmButton.vue"
import VarsityInlineConfirmGroup from "@/components/VarsityInlineConfirmGroup.vue"
import { createInlineConfirm } from "@/composables/createInlineConfirm"
import { type AcceptOrganizationRequestTask } from "@/model/app/accept-organization-request-task"
import SimilarOrganization from "@/pages/Tasks/Tasks/SimilarOrganization.vue"
import { useHttpClient } from "@/vf"
import { toRefs } from "vue"
import { type TasksProps } from "./tasks-props"

const props = defineProps<TasksProps<AcceptOrganizationRequestTask>>()

const emit = defineEmits<{
    (e: "updated"): void
}>()

const { task } = toRefs(props)

const http = useHttpClient()

const inlineConfirm = createInlineConfirm({
    async action() {
        await http.post(`/organization/${task.value.organizationId}/approve/${props.task.id}`, { approved: true })
    },
    afterAction() {
        emit("updated")
    },
})

const inlineDecline = createInlineConfirm({
    async action() {
        await http.post(`/organization/${task.value.organizationId}/approve/${props.task.id}`, { approved: false })
    },
    afterAction() {
        emit("updated")
    },
})
</script>

<template>
    <div class="p-4">
        <div class="d-flex justify-content-between align-items-center mb-4">
            <h3 class="mb-0">
                {{ $t(task.name!, task.nameProps) }}
            </h3>
            <div v-if="task.creatorName" class="d-flex justify-content-end align-items-center">
                <i class="fas fa-user mr-2"></i>
                <span class="text-primary">
                    {{ task.creatorName }}
                </span>
            </div>
        </div>
        <div class="row">
            <div class="col-md-6">
                <dl>
                    <dt>Name</dt>
                    <dd>
                        <h6 class="m-0">{{ task.organizationName }}</h6>
                    </dd>
                    <template v-if="task.address">
                        <dt>{{ $t("@tasks:tasks.accept_organization_request.registration_address") }}</dt>
                        <dd>
                            <ContactAddress :address="task.address"></ContactAddress>
                        </dd>
                    </template>
                </dl>
            </div>
            <div class="col-md-6">
                <dl>
                    <dt>Level</dt>
                    <dd>{{ $t("@app:organization.level." + task.organizationLevel) }}</dd>

                    <template v-if="task.organizationLevel !== 'main'">
                        <dt>{{ $t("@app:organization.level.main") }}</dt>
                        <dd>
                            <div>{{ task.mainOrganizationName }}</div>
                            <div class="small text-muted">{{ task.mainOrganizationNumber }}</div>
                        </dd>
                    </template>
                    <SimilarOrganization
                        v-if="props.viewMode === 'admin' && props.issue.owner"
                        :organization-id="issue.owner?.id!"
                        :task-id="task.id!"
                        class="mt-4"
                    ></SimilarOrganization>
                </dl>
            </div>
        </div>

        <div v-if="props.task.state === 'closed'" class="my-4 text-center">
            {{ $t("@tasks:tasks.accept_organization_request.task_approved") }}
        </div>

        <div v-if="props.task.state === 'rejected'" class="my-4 text-center">
            {{ $t("@tasks:tasks.accept_organization_request.task_rejected") }}
        </div>

        <div v-if="viewMode === 'admin' && task.state === 'open'" class="mt-4">
            <VarsityInlineConfirmGroup>
                <VarsityInlineConfirmButton class="btn btn-success btn-shape-skewed" :controller="inlineConfirm">
                    {{ $t("@tasks:tasks.accept_organization_request.approve") }}
                </VarsityInlineConfirmButton>
                <VarsityInlineConfirmButton class="btn btn-danger btn-shape-skewed" :controller="inlineDecline">
                    {{ $t("@tasks:tasks.accept_organization_request.decline") }}
                </VarsityInlineConfirmButton>
                <VarsityInlineConfirm :controller="inlineConfirm">
                    <template #confirmation>
                        {{ $t("@tasks:tasks.accept_organization_request.approve_confirm") }}
                    </template>
                    <template #success>
                        {{ $t("@tasks:tasks.accept_organization_request.approve_successfull") }}
                    </template>
                </VarsityInlineConfirm>
                <VarsityInlineConfirm :controller="inlineDecline">
                    <template #confirmation>
                        {{ $t("@tasks:tasks.accept_organization_request.decline_confirm") }}
                    </template>
                    <template #success>
                        {{ $t("@tasks:tasks.accept_organization_request.decline_successfull") }}
                    </template>
                </VarsityInlineConfirm>
            </VarsityInlineConfirmGroup>
        </div>
    </div>
</template>
